import { useCallback, useEffect, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import clsx from 'classnames';
import { useFormContext } from 'react-hook-form';
import parseHtml from 'html-react-parser';
import { Box, CircularProgress, makeStyles } from '@material-ui/core';
import { MDSButton, MDSTextField, MDSTheme, MDSTypography } from '@marqvision/mds/core';
import { CheckFillIcon, InfoBorderIcon, VerifiedFillIcon, VerifiedOutlineIcon } from '@marqvision/mds/icons';
import theme from '@marqvision/mds/core/Theme';
import { HttpStatusCodeEnum } from '@marqvision/shared/src/folio';
import NoticeText from '~/components/NoticeText';
import { LanguageEnum } from '~/constants/localizedString';
import { AuthProtocolSignupRequest, MarqCommonEnumsLanguageEnum } from '~/swagger/data-contracts';
import { AuthApi, useAuth, useLoginMutation, usePasswordResetMutation, useSignupMutation } from '~/apis/auth';
import { useLanguage, useBreakpoint } from '~/hooks';
import ConsentBox from '../ConsentBox';

interface IProps {
  variant: 'normal' | 'dense';
  onSignIn?: () => void;
  onSignUp?: () => void;
  onVerified?: () => void;
  omitSignUpAndConsent?: boolean;
}

const useStyles = makeStyles(() => ({
  title: {
    marginBottom: '8px',
  },
  titleDense: {
    marginBottom: '8px',
  },
}));

const ResendVerificationTimerSecond = 5;
const PasswordValidationRegex = /(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[@#$%^&+=!?`~*()_\-\[\]{}|;:'",.<>\/\\]).*$/;
const PasswordMinLength = 8;

const parseLanguage = (language: LanguageEnum): MarqCommonEnumsLanguageEnum => {
  switch (language) {
    case LanguageEnum.English:
      return MarqCommonEnumsLanguageEnum.En;
    default:
      return MarqCommonEnumsLanguageEnum.Ko;
  }
};

const SignInUp = ({ variant, onSignIn, onSignUp, onVerified, omitSignUpAndConsent = false }: IProps): JSX.Element => {
  const classes = useStyles();

  const { data } = useAuth();
  const tId = useRef<number>();
  const accessToken = data?.accessToken;
  const isLoggedIn = Boolean(accessToken);

  const { Lang, language } = useLanguage();
  const loginMutation = useLoginMutation();
  const signUpMutation = useSignupMutation();
  const passwordResetMutation = usePasswordResetMutation();
  const { mobile, getMediaQueryValue: mq } = useBreakpoint();

  const { getValues, register, watch, setValue, reset, formState } = useFormContext<AuthProtocolSignupRequest>();

  const [isForgotPassword, setIsForgotPassword] = useState<boolean>();
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [loginErrorCode, setLoginErrorCode] = useState<number>();
  const [timer, setTimer] = useState<number>(0);
  const [isSendingVerifyEmail, setIsSendingVerifyEmail] = useState<boolean>(false);
  const [isVerifyProgress, setIsVerifyProgress] = useState<boolean>(false);
  const [isVerified, setIsVerified] = useState<boolean>();
  const [emailValidationMessage, setEmailValidationMessage] = useState<string>('');
  const [initFormValid, setInitFormValid] = useState(false);
  const [haveAccount, setHaveAccount] = useState(false);

  const token = watch('token');
  const email = watch('email');

  const password = watch('password');
  const termsAgreement = watch('termsAgreement');

  const isDense = variant === 'dense';
  const gapBetweenItem = isDense || mobile ? '40px' : '24px';

  const disableLogin = !(email && password) && !initFormValid;

  const handleFormInit = (e: React.AnimationEvent<HTMLDivElement>) => {
    const target = e.target as Element;
    const autofilled = !!target?.matches?.('*:-webkit-autofill');

    setInitFormValid(autofilled);
  };

  const validateEmail = (): boolean => {
    const isInvalid = !!formState.errors.email;
    setEmailValidationMessage(formState.errors.email?.message || '');
    return isInvalid;
  };

  const handleVerifyEmailClick = async () => {
    if (validateEmail() || isSendingVerifyEmail) {
      return;
    }
    setIsSendingVerifyEmail(true);

    const verifySendFn = isForgotPassword ? AuthApi.findVerifySendCreate : AuthApi.signupVerifySendCreate;
    try {
      const resp = await verifySendFn({ email, language: parseLanguage(language) });
      const { token } = resp.data;
      setValue('token', token, { shouldValidate: true });
    } catch (error) {
      switch ((error as AxiosError).response?.status) {
        case HttpStatusCodeEnum.EmailAlreadyExists:
          setEmailValidationMessage(Lang.EmailAlreadyRegistered);
          break;

        case HttpStatusCodeEnum.EmailDoesNotExist:
          setEmailValidationMessage(Lang.EmailDoesNotExist);
          break;

        default:
          isForgotPassword
            ? setEmailValidationMessage(Lang.EmailDoesNotExist)
            : setEmailValidationMessage(Lang.EmailAlreadyRegistered);
      }
    } finally {
      setIsSendingVerifyEmail(false);
    }
  };

  const handleCompleteVerificationClick = async () => {
    if (!token || isVerifyProgress) {
      return;
    }

    setIsVerifyProgress(true);

    const verifyCheckFn = isForgotPassword ? AuthApi.findVerifyCheckCreate : AuthApi.signupVerifyCheckCreate;

    try {
      const { data } = await verifyCheckFn({ email, token });
      const { verified } = data;

      setIsVerified(verified);

      reset({ ...getValues(), password: '' });
      setConfirmPassword('');

      if (!isForgotPassword && verified) {
        onVerified?.();
      }
    } finally {
      setIsVerifyProgress(false);
    }
  };

  const handleSignUpClick = async ({
    email,
    password,
    termsAgreement,
    token,
    referralCode,
  }: AuthProtocolSignupRequest) => {
    if (isForgotPassword) {
      await passwordResetMutation.mutateAsync({ email, password, token });
    } else {
      await signUpMutation.mutateAsync({ email, password, termsAgreement, token, referralCode });
    }
    onSignUp?.();
  };

  const handleLoginClick = async ({ email, password }: AuthProtocolSignupRequest) => {
    try {
      await loginMutation.mutateAsync({ email, password });
      setLoginErrorCode(undefined);
      onSignIn?.();
    } catch (error) {
      setLoginErrorCode((error as AxiosError)?.response?.status);
    }
  };

  const initialize = useCallback(() => {
    setIsForgotPassword(undefined);
    setConfirmPassword('');
    setLoginErrorCode(undefined);
    setTimer(0);
    setIsVerifyProgress(false);
    setIsVerified(undefined);

    reset({ ...getValues(), email: '', password: '', termsAgreement: false, token: '', referralCode: '' });
  }, [getValues, reset]);

  const renderLoginSuccessful = (): JSX.Element => (
    <Box marginBottom={gapBetweenItem} display="flex" marginTop="64px" alignItems="center" justifyContent="center">
      <VerifiedFillIcon color={MDSTheme.palette.green[700]} />
      <MDSTypography variant="T24" weight="medium" style={{ marginLeft: '9px', color: 'green' }}>
        {Lang.LoginSuccessful}
      </MDSTypography>
    </Box>
  );

  useEffect(() => {
    if (!token) {
      return;
    }
    setTimer(ResendVerificationTimerSecond);

    tId.current = window.setInterval(() => {
      setTimer((ps) => {
        if (ps === 0) {
          clearInterval(tId?.current);
        }

        return Math.max(ps - 1, 0);
      });
    }, 1000);

    return () => clearInterval(tId?.current);
  }, [token]);

  useEffect(() => {
    if (password?.length === 0) {
      setLoginErrorCode(undefined);
    }
  }, [password]);

  useEffect(() => {
    clearInterval(tId?.current);
    setValue('token', '', { shouldValidate: true });
  }, [email, setValue]);

  useEffect(() => {
    initialize();
    setHaveAccount(true);
  }, [initialize]);

  return (
    <Box gridGap={gapBetweenItem}>
      {haveAccount && !isForgotPassword && (
        <Box
          marginTop={gapBetweenItem}
          width="100%"
          component="form"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !disableLogin) {
              if (validateEmail()) {
                return;
              }
              handleLoginClick(getValues());
            }
          }}
        >
          <MDSTypography weight="medium" variant={mq('T20', 'T20', 'T20')}>
            {Lang.SignInToYourAccount}
          </MDSTypography>
          <div>
            <MDSTypography
              variant={mq('T14', 'T14', 'T14')}
              weight="medium"
              className={clsx(classes.title, { [classes.titleDense]: isDense })}
              style={{ marginTop: '32px' }}
            >
              {Lang.EmailAddress}
            </MDSTypography>
            <MDSTextField
              onAnimationStart={handleFormInit}
              autoFocus
              size={mq('large', 'large', 'large')}
              placeholder="official@company.com"
              {...register('email', {
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: Lang.EnterValidEmail,
                },
              })}
              fullWidth
              readOnly={!!accessToken}
              error={loginErrorCode === HttpStatusCodeEnum.UnrecognizedUser}
            />
            {!emailValidationMessage && loginErrorCode === HttpStatusCodeEnum.UnrecognizedUser && (
              <NoticeText variant="alert">{Lang.EmailDoesNotExist}</NoticeText>
            )}
            {emailValidationMessage && <NoticeText variant="alert">{emailValidationMessage}</NoticeText>}
          </div>

          {!isLoggedIn && (
            <>
              <Box>
                <MDSTypography
                  variant={mq('T14', 'T14', 'T14')}
                  weight="medium"
                  className={clsx(classes.title, { [classes.titleDense]: isDense })}
                  style={{ marginTop: '20px' }}
                >
                  {Lang.Password}
                </MDSTypography>
                <MDSTextField
                  size={mq('large', 'large', 'large')}
                  placeholder={Lang.Password}
                  type="password"
                  {...register('password')}
                  fullWidth
                  error={loginErrorCode === HttpStatusCodeEnum.UnrecognizedPassword}
                />
                {loginErrorCode === HttpStatusCodeEnum.UnrecognizedPassword && (
                  <NoticeText variant="alert">{Lang.IncorrectPassword}</NoticeText>
                )}
              </Box>
              <MDSButton
                variant="text"
                color="blue"
                size="medium"
                onClick={() => setIsForgotPassword(true)}
                style={{ marginTop: '12px' }}
              >
                {Lang.ForgotPassword}
              </MDSButton>
              <Box display="flex" alignItems="center" flexDirection="column" gridGap="24px">
                <MDSButton
                  variant="fill"
                  color="blue"
                  size={mq('large', 'large', 'large')}
                  style={{ width: '100%', marginTop: '24px' }}
                  onClick={() => {
                    if (validateEmail()) {
                      return;
                    }

                    if (loginMutation.isLoading) {
                      return;
                    }

                    handleLoginClick(getValues());
                  }}
                  disabled={disableLogin || loginMutation.isLoading}
                >
                  {loginMutation.isLoading && (
                    <CircularProgress size={20} thickness={6} style={{ marginRight: '6px', color: 'white' }} />
                  )}
                  {loginMutation.isLoading ? Lang.LoggingIn : Lang.Login}
                </MDSButton>
              </Box>
              <Box marginTop="32px" display="flex" alignItems="center" gridGap="6px">
                <MDSTypography>{Lang.NewToMARQFolio}</MDSTypography>
                <MDSButton variant="text" color="blue" size="medium" onClick={() => setHaveAccount(false)}>
                  {Lang.CreateAnAccount}
                </MDSButton>
              </Box>
            </>
          )}

          {isLoggedIn && renderLoginSuccessful()}
          {!isLoggedIn && (
            <MDSTypography
              variant="T13"
              weight="regular"
              style={{
                color: MDSTheme.palette.bluegray[500],
                textAlign: 'left',
                letterSpacing: 0,
                paddingTop: '12px',
                lineHeight: '20px',
              }}
            >
              {parseHtml(Lang.YouMustCreateSeperateAccount)}
            </MDSTypography>
          )}
        </Box>
      )}
      {/* 2-A End */}

      {/* 2-B. Don't have a MARQ Folio account or forgot password */}
      {(haveAccount === false || isForgotPassword) && (
        <Box marginTop={gapBetweenItem} width="100%">
          <Box>
            <MDSTypography variant="T20" weight="medium">
              {Lang.VerifyEmail}
            </MDSTypography>
            <MDSTypography
              variant="T14"
              weight="medium"
              className={clsx(classes.title, { [classes.titleDense]: isDense })}
              style={{ marginTop: '32px' }}
            >
              {Lang.EmailAddress}
            </MDSTypography>
            <MDSTextField
              size="large"
              placeholder="official@company.com"
              {...register('email', {
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: Lang.EnterValidEmail,
                },
              })}
              fullWidth
              readOnly={isVerified}
            />
            {emailValidationMessage && <NoticeText variant="alert">{emailValidationMessage}</NoticeText>}
          </Box>

          {!isVerified && (
            <Box marginTop={gapBetweenItem}>
              <Box>
                <MDSButton
                  variant="fill"
                  color="blue"
                  size="large"
                  style={{ width: '100%', marginBottom: '12px' }}
                  disabled={!email || email.length === 0 || !!token}
                  onClick={handleVerifyEmailClick}
                >
                  {isSendingVerifyEmail && (
                    <>
                      <CircularProgress size={20} thickness={6} style={{ color: 'white' }} />
                      <span style={{ marginLeft: '4px' }}>{Lang.VerifyEmailSending}</span>
                    </>
                  )}
                  {!isSendingVerifyEmail && token && Lang.VerifyEmailSent}
                  {!isSendingVerifyEmail && !token && Lang.VerifyEmail}
                </MDSButton>
                {token && (
                  <Box display="flex" justifyContent="center">
                    {timer > 0 && (
                      <MDSTypography variant="T16" weight="medium" style={{ color: theme.palette.bluegray[700] }}>
                        {Lang.SendAgainAfterNSeconds(timer)}
                      </MDSTypography>
                    )}
                    {timer === 0 && (
                      <MDSButton variant="text" color="blue" size="large" onClick={handleVerifyEmailClick}>
                        {Lang.ResendVerifyEmail}
                      </MDSButton>
                    )}
                  </Box>
                )}
              </Box>
              {token && (
                <>
                  <MDSTypography
                    variant={mq('T14', 'T14', 'T14')}
                    style={{
                      width: '100%',
                      color: MDSTheme.palette.bluegray[800],
                      lineHeight: '150%',
                      marginTop: '32px',
                    }}
                  >
                    <div>{Lang.ToConfirmYour}</div>
                    <div>{Lang.GoToYourEmail}</div>
                    <div>{Lang.ReturnToThisScreen}</div>
                  </MDSTypography>
                  <Box
                    display="flex"
                    justifyContent="center"
                    flexDirection="column"
                    width="100%"
                    gridGap="4px"
                    marginTop="32px"
                  >
                    <MDSButton
                      variant="fill"
                      color="green"
                      size="large"
                      fullWidth
                      onClick={handleCompleteVerificationClick}
                    >
                      {isVerifyProgress ? (
                        <>
                          <CircularProgress size={20} thickness={6} style={{ color: 'white' }} />
                          <span style={{ marginLeft: '4px' }}>{Lang.Checking}</span>
                        </>
                      ) : (
                        <>
                          <VerifiedOutlineIcon />
                          <span style={{ marginLeft: '4px' }}>{Lang.CompleteVerification}</span>
                        </>
                      )}
                    </MDSButton>
                    {isVerified === false && <NoticeText variant="alert">{Lang.VerificationIncomplete}</NoticeText>}
                  </Box>
                </>
              )}
            </Box>
          )}

          {isVerified && (
            <Box marginTop="6px">
              <Box display="flex" alignItems="center">
                <CheckFillIcon scale={0.8} color={MDSTheme.palette.green[700]} />
                <MDSTypography
                  variant="T14"
                  weight="medium"
                  style={{ marginLeft: '6px', color: MDSTheme.palette.green[700] }}
                >
                  {Lang.Verified}
                </MDSTypography>
              </Box>
              <Box width="100%">
                <MDSTypography
                  variant={mq('T14', 'T14', 'T14')}
                  weight="medium"
                  className={clsx(classes.title, { [classes.titleDense]: isDense })}
                  style={{ marginTop: '20px' }}
                >
                  {Lang.Password}
                </MDSTypography>
                <Box className={clsx(classes.title, { [classes.titleDense]: isDense })}>
                  <MDSTextField
                    size="large"
                    placeholder={Lang.Password}
                    type="password"
                    {...register('password', {
                      minLength: PasswordMinLength,
                      pattern: PasswordValidationRegex,
                    })}
                    fullWidth
                  />
                  <Box display="flex" alignItems="center" marginTop="6px">
                    <InfoBorderIcon
                      scale={0.6666}
                      color={
                        !password?.length
                          ? undefined
                          : password.length >= PasswordMinLength
                          ? undefined
                          : MDSTheme.palette.red[700]
                      }
                    />
                    <MDSTypography
                      variant="T14"
                      weight="medium"
                      color={
                        !password?.length
                          ? MDSTheme.palette.bluegray[800]
                          : password.length >= PasswordMinLength
                          ? MDSTheme.palette.bluegray[800]
                          : MDSTheme.palette.red[700]
                      }
                      style={{ marginLeft: '6px' }}
                    >
                      {Lang.MoreThan8Char}
                    </MDSTypography>
                  </Box>
                  <Box display="flex" alignItems="top" marginTop="6px">
                    <InfoBorderIcon
                      scale={0.6666}
                      color={
                        !password?.length
                          ? undefined
                          : PasswordValidationRegex.test(password)
                          ? undefined
                          : MDSTheme.palette.red[700]
                      }
                    />
                    <MDSTypography
                      variant="T14"
                      weight="medium"
                      color={
                        !password?.length
                          ? undefined
                          : PasswordValidationRegex.test(password)
                          ? undefined
                          : MDSTheme.palette.red[700]
                      }
                      style={{ marginLeft: '6px' }}
                    >
                      {Lang.IncludeAtLeast1Letter}
                    </MDSTypography>
                  </Box>
                </Box>
                <MDSTypography
                  variant={mq('T14', 'T14', 'T14')}
                  weight="medium"
                  className={clsx(classes.title, { [classes.titleDense]: isDense })}
                  style={{ marginTop: '20px' }}
                >
                  {Lang.PasswordConfirmation}
                </MDSTypography>
                <MDSTextField
                  size={mq('large', 'large', 'large')}
                  placeholder={Lang.PasswordConfirmation}
                  type="password"
                  value={confirmPassword}
                  onChange={(_, p) => setConfirmPassword(p as string)}
                  fullWidth
                  style={{ marginTop: '8px' }}
                />
                {confirmPassword.length !== 0 && confirmPassword === password && (
                  <NoticeText variant="success">{Lang.Confirmed}</NoticeText>
                )}
              </Box>

              {!isForgotPassword && (
                <Box width="100%" style={{ marginTop: '20px' }}>
                  <MDSTypography
                    variant="T14"
                    weight="medium"
                    className={clsx(classes.title, { [classes.titleDense]: isDense })}
                  >
                    {Lang.ReferralCode}
                  </MDSTypography>
                  <MDSTextField size="large" placeholder={Lang.Optional} {...register('referralCode')} fullWidth />
                </Box>
              )}
              {!omitSignUpAndConsent && !isForgotPassword && (
                <ConsentBox
                  checked={!!termsAgreement}
                  onChange={(_: unknown, checked: boolean) =>
                    setValue('termsAgreement', checked, { shouldValidate: true })
                  }
                />
              )}
              {(!omitSignUpAndConsent || isForgotPassword) && !isLoggedIn && (
                <MDSButton
                  variant="fill"
                  color="blue"
                  size={mq('large', 'extra_large', 'extra_large')}
                  style={{ width: '100%', marginTop: '24px' }}
                  onClick={() => {
                    if (signUpMutation.isLoading || passwordResetMutation.isLoading) {
                      return;
                    }

                    handleSignUpClick(getValues());
                  }}
                  disabled={
                    (!isForgotPassword && !formState.isValid) ||
                    confirmPassword.length === 0 ||
                    confirmPassword !== password ||
                    (!omitSignUpAndConsent && !isForgotPassword && !termsAgreement) ||
                    signUpMutation.isLoading ||
                    passwordResetMutation.isLoading
                  }
                >
                  {(signUpMutation.isLoading || passwordResetMutation.isLoading) && (
                    <CircularProgress size={20} thickness={6} style={{ marginRight: '6px', color: 'white' }} />
                  )}
                  {isForgotPassword &&
                    (passwordResetMutation.isLoading ? Lang.ResetPasswordAndLoggingIn : Lang.ResetPasswordAndLogin)}
                  {!isForgotPassword && (signUpMutation.isLoading ? Lang.SigningUp : Lang.SignUp)}
                </MDSButton>
              )}
              {isLoggedIn && renderLoginSuccessful()}
            </Box>
          )}
          {(isForgotPassword || !haveAccount) && !token && (
            <MDSButton
              variant="text"
              color="blue"
              size="medium"
              onClick={() => {
                setHaveAccount(true);
                setIsForgotPassword(false);
                setValue('password', '');
              }}
              style={{ marginTop: '20px', justifyContent: 'flex-start' }}
            >
              {Lang.IAlreadySignedUp}
            </MDSButton>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SignInUp;
